import { gql } from "@apollo/client";

const DATE_RULE_FIELDS = `
  mode
  fixedDate
  daysAfterToday
  daysAfterStart
  allowedDaysOfWeek
`;

const SELECTION_FIELDS = `
  sort {
    field
    direction
    valueOrder
  }
  preferred {
    mode
    items {
      id
      name
    }
  }
`;

export const GET_TRAVEL_DEALS_CONFIGURATIONS = gql`
  query GetTravelDealsConfigurations($name: String, $pageSize: Int = 50, $pageNumber: Int = 0, $includeDisabled: Boolean = false) {
    travelDealsConfigurations(name: $name, pageSize: $pageSize, pageNumber: $pageNumber, includeDisabled: $includeDisabled) {
      count
      pageSize
      pageNumber
      items {
        id
        name
        kind
        isEnabled
        maxOffers
        isAllCities
        cities
        citiesConfig {
          defaultRadiusKm
          cities {
            key
            name
            latitude
            longitude
            radiusKm
          }
        }
        flight {
          originAirport
          originAirportName
          destinationAirport
          destinationAirportName
          departureDateRule { ${DATE_RULE_FIELDS} }
          returnDateRule { ${DATE_RULE_FIELDS} }
          numberOfAdults
          passengerNationality
          searchType
          classFlight
          luggage
          selection { ${SELECTION_FIELDS} }
        }
        hotel {
          destinationId
          destinationName
          hotelId
          hotelName
          checkinDateRule { ${DATE_RULE_FIELDS} }
          numNights
          numAdults
          roomQuantity
          selection { ${SELECTION_FIELDS} }
        }
        car {
          pickupLocationCode
          pickupLocationName
          dropoffLocationCode
          dropoffLocationName
          locationType
          pickupDateRule { ${DATE_RULE_FIELDS} }
          dropoffDateRule { ${DATE_RULE_FIELDS} }
          pickupHour
          pickupMinutes
          dropoffHour
          dropoffMinutes
          selection { ${SELECTION_FIELDS} }
        }
      }
    }
  }
`;

export const GET_TRAVEL_DEALS_CONFIGURATION_BY_ID = gql`
  query GetTravelDealsConfigurationById($id: UUID!) {
    travelDealsConfigurationById(id: $id) {
      id
      name
      kind
      isEnabled
      maxOffers
      isAllCities
      cities
      citiesConfig {
        defaultRadiusKm
        cities {
          key
          name
          latitude
          longitude
          radiusKm
        }
      }
      flight {
        originAirport
        originAirportName
        destinationAirport
        destinationAirportName
        departureDateRule { ${DATE_RULE_FIELDS} }
        returnDateRule { ${DATE_RULE_FIELDS} }
        numberOfAdults
        passengerNationality
        searchType
        classFlight
        luggage
        selection { ${SELECTION_FIELDS} }
      }
      hotel {
        destinationId
        destinationName
        hotelId
        hotelName
        checkinDateRule { ${DATE_RULE_FIELDS} }
        numNights
        numAdults
        roomQuantity
        selection { ${SELECTION_FIELDS} }
      }
      car {
        pickupLocationCode
        pickupLocationName
        dropoffLocationCode
        dropoffLocationName
        locationType
        pickupDateRule { ${DATE_RULE_FIELDS} }
        dropoffDateRule { ${DATE_RULE_FIELDS} }
        pickupHour
        pickupMinutes
        dropoffHour
        dropoffMinutes
        selection { ${SELECTION_FIELDS} }
      }
    }
  }
`;

export const GET_COMBO_OFFERS_CITY_CONFIGURATIONS = gql`
  query GetComboOffersCityConfigurations($cityName: String, $pageSize: Int = 50, $pageNumber: Int = 0, $includeDisabled: Boolean = false) {
    comboOffersCityConfigurations(cityName: $cityName, pageSize: $pageSize, pageNumber: $pageNumber, includeDisabled: $includeDisabled) {
      count
      pageSize
      pageNumber
      items {
        id
        cityName
        isEnabled
        maxOffersPerType
        flights {
          maxOffers
          originAirport
          originAirportName
          destinationAirport
          destinationAirportName
          departureDateRule { ${DATE_RULE_FIELDS} }
          returnDateRule { ${DATE_RULE_FIELDS} }
          numberOfAdults
          passengerNationality
          searchType
          classFlight
          luggage
          selection { ${SELECTION_FIELDS} }
        }
        hotels {
          maxOffers
          destinationId
          destinationName
          checkinDateRule { ${DATE_RULE_FIELDS} }
          numNights
          numAdults
          roomQuantity
          selection { ${SELECTION_FIELDS} }
        }
        rentACars {
          maxOffers
          pickupLocationCode
          pickupLocationName
          dropoffLocationCode
          dropoffLocationName
          locationType
          pickupDateRule { ${DATE_RULE_FIELDS} }
          dropoffDateRule { ${DATE_RULE_FIELDS} }
          pickupHour
          pickupMinutes
          dropoffHour
          dropoffMinutes
          selection { ${SELECTION_FIELDS} }
        }
      }
    }
  }
`;

export const GET_COMBO_OFFERS_CITY_CONFIGURATION_BY_ID = gql`
  query GetComboOffersCityConfigurationById($id: UUID!) {
    comboOffersCityConfigurationById(id: $id) {
      id
      cityName
      isEnabled
      maxOffersPerType
      flights {
        maxOffers
        originAirport
        originAirportName
        destinationAirport
        destinationAirportName
        departureDateRule { ${DATE_RULE_FIELDS} }
        returnDateRule { ${DATE_RULE_FIELDS} }
        numberOfAdults
        passengerNationality
        searchType
        classFlight
        luggage
        selection { ${SELECTION_FIELDS} }
      }
      hotels {
        maxOffers
        destinationId
        destinationName
        checkinDateRule { ${DATE_RULE_FIELDS} }
        numNights
        numAdults
        roomQuantity
        selection { ${SELECTION_FIELDS} }
      }
      rentACars {
        maxOffers
        pickupLocationCode
        pickupLocationName
        dropoffLocationCode
        dropoffLocationName
        locationType
        pickupDateRule { ${DATE_RULE_FIELDS} }
        dropoffDateRule { ${DATE_RULE_FIELDS} }
        pickupHour
        pickupMinutes
        dropoffHour
        dropoffMinutes
        selection { ${SELECTION_FIELDS} }
      }
    }
  }
`;

/** Airline lookup for the flight preference picker (same list the airlines screen manages). */
export const SEARCH_TRAVEL_AIRLINES = gql`
  query SearchTravelAirlinesForDeals($searchTerm: String, $pageSize: Int! = 15) {
    travelAirlines(searchTerm: $searchTerm, includeInactive: false, pageSize: $pageSize, pageNumber: 0) {
      items {
        id
        iataCode
        name
      }
    }
  }
`;
